import { useEffect, useRef, useState } from 'react'

/**
 * Drop-in replacement for useState that mirrors the value into sessionStorage
 * keyed by the provided string. Used by list pages (Quotes, Work Orders,
 * Invoices, Clients, …) so a user who clicks into a summary screen and then
 * clicks the top-left "Back" link finds the same filters, sort and pagination
 * the list had before.
 *
 * sessionStorage is per-tab and clears on tab close, so this doesn't leak
 * between users on shared machines or across separate browsing sessions.
 */
export const useSessionState = (key, initial) => {
  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined' || !key) return initial
    try {
      const raw = window.sessionStorage.getItem(key)
      return raw != null ? JSON.parse(raw) : initial
    } catch {
      return initial
    }
  })

  // Track the latest key so a remount with a different key doesn't clobber
  // the cache by writing the previous component's value under the new key.
  const keyRef = useRef(key)
  useEffect(() => {
    keyRef.current = key
  }, [key])

  useEffect(() => {
    if (typeof window === 'undefined' || !keyRef.current) return
    try {
      window.sessionStorage.setItem(keyRef.current, JSON.stringify(value))
    } catch {
      /* ignore quota / serialisation errors */
    }
  }, [value])

  return [value, setValue]
}
